import type { Cents } from './money.ts';

// Caja: efectivo esperado, conteo por billetes, ingresos/retiros/gastos.
// Cuenta corriente de clientes: saldo = suma de movimientos (deuda +, pagos −).
// El servidor recalcula lo mismo (app.cash_session_expected); si cambia una regla acá,
// tiene que cambiar allá.

// ---------------------------------------------------------------------------
// Movimientos de caja
// ---------------------------------------------------------------------------

export const CASH_MOVEMENT_TYPES = {
  in: { label: 'Ingreso', sign: 1, hint: 'Ej.: cambio que trajiste, fondo extra.' },
  out: { label: 'Retiro', sign: -1, hint: 'Ej.: el dueño se lleva efectivo, depósito en el banco.' },
  expense: { label: 'Gasto', sign: -1, hint: 'Ej.: pago a un proveedor o un flete en efectivo.' },
} as const;

export type CashMovementType = keyof typeof CASH_MOVEMENT_TYPES;

/** Billetes y monedas argentinos para contar la caja (centavos). */
export const ARS_DENOMINATIONS: readonly Cents[] = [
  2_000_000, 1_000_000, 200_000, 100_000, 50_000, 20_000, 10_000, 5_000, 2_000, 1_000, 500, 200, 100,
];

/** Total de un conteo por billetes: { valor en centavos: cantidad }. */
export function countTotal(counts: Record<string, number>): Cents {
  return Object.entries(counts).reduce((sum, [value, qty]) => sum + Number(value) * (Number.isFinite(qty) && qty > 0 ? Math.floor(qty) : 0), 0);
}

export interface CashSaleInput {
  kind: 'sale' | 'return';
  payments: Array<{ method: string; amount: Cents }>;
}

export interface CashMovementInput {
  type: CashMovementType;
  amount: Cents;
}

/** Pago de cuenta corriente cobrado en esta caja. */
export interface AccountPaymentInput {
  method: string | null;
  /** Negativo: baja la deuda del cliente. */
  amount: Cents;
}

export interface CashSummary {
  opening: Cents;
  /** Efectivo cobrado en ventas (lo que quedó en la caja, ya descontado el vuelto). */
  cashSales: Cents;
  /** Efectivo devuelto en devoluciones. */
  cashRefunds: Cents;
  /** Pagos de cuenta corriente cobrados en efectivo. */
  accountPayments: Cents;
  ins: Cents;
  outs: Cents;
  expenses: Cents;
  /** Lo que tendría que haber en la caja. */
  expected: Cents;
  /** Ventas por medio de pago (sin restar devoluciones). */
  byMethod: Record<string, Cents>;
  /** Devoluciones por medio. */
  refundsByMethod: Record<string, Cents>;
  salesCount: number;
  returnsCount: number;
  salesTotal: Cents;
  returnsTotal: Cents;
}

export function cashSummary(
  opening: Cents,
  sales: CashSaleInput[],
  movements: CashMovementInput[],
  accountPayments: AccountPaymentInput[] = [],
): CashSummary {
  const s: CashSummary = {
    opening,
    cashSales: 0,
    cashRefunds: 0,
    accountPayments: 0,
    ins: 0,
    outs: 0,
    expenses: 0,
    expected: 0,
    byMethod: {},
    refundsByMethod: {},
    salesCount: 0,
    returnsCount: 0,
    salesTotal: 0,
    returnsTotal: 0,
  };
  for (const sale of sales) {
    const target = sale.kind === 'return' ? s.refundsByMethod : s.byMethod;
    if (sale.kind === 'return') s.returnsCount++;
    else s.salesCount++;
    for (const p of sale.payments) {
      target[p.method] = (target[p.method] ?? 0) + p.amount;
      if (sale.kind === 'return') s.returnsTotal += p.amount;
      else s.salesTotal += p.amount;
      if (p.method === 'cash') {
        if (sale.kind === 'return') s.cashRefunds += p.amount;
        else s.cashSales += p.amount;
      }
    }
  }
  for (const m of movements) {
    if (m.type === 'in') s.ins += m.amount;
    else if (m.type === 'out') s.outs += m.amount;
    else s.expenses += m.amount;
  }
  for (const p of accountPayments) if (p.method === 'cash') s.accountPayments += -p.amount;
  s.expected = opening + s.cashSales - s.cashRefunds + s.accountPayments + s.ins - s.outs - s.expenses;
  return s;
}

// ---------------------------------------------------------------------------
// Clientes y cuenta corriente
// ---------------------------------------------------------------------------

export const ACCOUNT_ENTRY_TYPES = {
  sale: 'Compra a cuenta',
  return: 'Devolución',
  payment: 'Pago',
  adjustment: 'Ajuste',
} as const;

export type AccountEntryType = keyof typeof ACCOUNT_ENTRY_TYPES;

export const DOC_TYPES = ['DNI', 'CUIT', 'CUIL'] as const;
export type DocType = (typeof DOC_TYPES)[number];

export const CUSTOMER_TAX_CONDITIONS = {
  consumidor_final: 'Consumidor final',
  monotributo: 'Monotributo',
  responsable_inscripto: 'Responsable inscripto',
  exento: 'Exento',
} as const;
export type CustomerTaxCondition = keyof typeof CUSTOMER_TAX_CONDITIONS;

/** Documento sin puntos ni guiones; null si no es válido para el tipo. */
export function normalizeDoc(type: DocType, value: string): string | null {
  const digits = value.replace(/\D/g, '');
  if (type === 'DNI') return /^\d{7,8}$/.test(digits) ? digits : null;
  return /^\d{11}$/.test(digits) ? digits : null;
}

export function validateCustomerName(name: string): string | null {
  const n = name.trim();
  if (n.length < 2) return 'Escribí el nombre (al menos 2 letras).';
  if (n.length > 80) return 'Máximo 80 caracteres.';
  return null;
}

/** ¿Una compra a cuenta supera el límite de crédito? (sin límite = nunca). */
export function exceedsCreditLimit(balance: Cents, amount: Cents, limit: Cents | null | undefined): boolean {
  return limit != null && balance + amount > limit;
}

/** "Debe $ 1.500" / "A favor $ 200" / "Al día". */
export function balanceLabel(balance: Cents, money: (c: Cents) => string): string {
  if (balance > 0) return `Debe ${money(balance)}`;
  if (balance < 0) return `A favor ${money(-balance)}`;
  return 'Al día';
}
